import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { AnalyticsState } from "./analytics-state";
import { AnalyticsData, GASv3 } from "./gasv3";
import { Gtm } from "./gtm-integration";
import { Helpers } from "./helpers";
import { MetricKey, Metrics } from "./metrics";
import { Tracking } from "./tracking";

/**
 * Module for initializing the wrapper.
 * @module Initializer
 */
export class Initializer {
    /**
     * Loads and initializes segment.io.
     * @function initWithPageAnalytics
     * @param {string} writeKey - required, Segment.io key
     * @param {object} pageViewProperties - optional, dictionary of properties of the page (see: https://segment.com/docs/libraries/analytics.js/#page)
     * @param {object} loadOptions - optional, object with a dictionary of which integrations to load (see: https://segment.com/docs/sources/website/analytics.js/#load-options)
     * @param {number} loadTimestamp - optional, the timestamp at which the initializing site loaded this script
     * @param {string} anonymousId - GASv3 analytics client anonymous id
     * @param {function} createAnalyticsEvent - analytics next CreateUIAnalyticsEvent
     */
    public static initWithPageAnalytics(
        writeKey: string,
        pageViewProperties: AnalyticsData,
        loadOptions: object,
        loadTimestamp: number,
        anonymousId: string,
        createAnalyticsEvent: CreateUIAnalyticsEvent | null
    ) {
        AnalyticsState.anonymousId = anonymousId;
        AnalyticsState.createAnalyticsEvent = createAnalyticsEvent;

        // set load timestamp for calculating metrics
        Metrics.setLoadTimestamp(loadTimestamp);
        // TODO: Switch these params to dynamically config based on writeKey
        Gtm.initialize(writeKey);

        Initializer.captureUTMs();

        // set unpopulated params to empty objects
        if (typeof loadOptions !== "object" || loadOptions === null) {
            loadOptions = {};
        }

        if (
            typeof pageViewProperties !== "object" ||
            pageViewProperties === null
        ) {
            pageViewProperties = {};
        }

        // adding marketing boolean if current product comes under marketing domain
        Initializer.setIsMarketingEvent(pageViewProperties);

        // store params for later reporting if necessary.
        AnalyticsState.writeKey = writeKey;

        // TODO: pageViewOptions used to be passed in, but doesn't appear to be used outside of Segment.
        // Can we just delete this completely?
        AnalyticsState.pageViewOptions = {};

        // auto-fire page view
        Helpers.addActualUrl(pageViewProperties);

        Tracking.pageView(pageViewProperties);

        // add auto tracking and configure google analytics integration
        Initializer.autoTrack();
        Initializer.gaIntegration();

        Initializer.globalCallReady();
    }

    /**
     * Calls window functions that are awaiting a wrapper ready state
     * @function globalCallReady
     */
    public static globalCallReady() {
        if (typeof window.analyticsPageReady === "function") {
            window.analyticsPageReady();
        }

        Metrics.logMetric(MetricKey.TIME_TO_READY);
        Metrics.sendMetrics();
    }

    public static captureUTMs() {
        const utmKeys = {
            "utm_sfdc-campaign_id": "",
            "utm_campaign": "",
            "utm_medium": "",
            "utm_source": "",
            "utm_content": "",
            "utm_term": ""
        };

        AnalyticsState.utms = Helpers.getAndSetUtms(utmKeys);
    }

    /**
     * Applies auto-tracking for select domains
     * @function autoTrack
     */
    public static autoTrack() {
        Tracking.autoTrackButtons(true);
        Tracking.autoTrackLinks(true);
        Tracking.autoTrackVideos();
        Tracking.autoTrackSelects();
        Tracking.autoTrackTabs();
    }

    /**
     * Completes path overrides for Google Analytics
     * @function gaIntegration
     */
    public static gaIntegration() {
        const emailDetected = Helpers.emailAddressIsDetected();
        const pathOverride = "/path-contained-email-address";

        if (typeof window.ga !== "undefined") {
            window.ga("require", "linker");
            window.ga("linker:autoLink", [
                "atlassian.com",
                "bitbucket.org",
                "hipchat.com",
                "attendease.com",
                "get.atlassian.com",
                "eegeventsite.secure.force.com",
                "atlassian.swoogo.com"
            ]);

            if (emailDetected) {
                // only override what is needed
                if (Helpers.urlContainsEmail(window.location.href)) {
                    window.ga(
                        "set",
                        "location",
                        window.location.origin + pathOverride
                    );
                    window.ga("set", "path", pathOverride);
                }
                if (Helpers.urlContainsEmail(document.referrer)) {
                    window.ga(
                        "set",
                        "referrer",
                        Helpers.hostnameFromUrl(document.referrer) +
                            pathOverride
                    );
                }
            }
        }
    }

    public static setIsMarketingEvent(
        pageViewProperties: Record<string, unknown>
    ) {
        if (typeof pageViewProperties["isMarketingEvent"] !== "boolean") {
            GASv3.isMarketingEvent = true;
        } else {
            GASv3.isMarketingEvent = pageViewProperties["isMarketingEvent"];
        }
    }

    public static setXID(xid: number) {
        Tracking.xid = xid;
    }
}
